import type { HelpContent } from './types';

export const settingsUserPermissionsHelp: HelpContent = {
  pageTitle: 'Roles & Permissions',
  description: 'Roles & Permissions is where administrators create and manage the roles that control what each user can see and do across the platform.',

  functionality: {
    overview:
      'This page lists all platform roles. Each role has a defined set of permissions covering project visibility, project actions, project tab access, report access, and settings access. Users are assigned to one role, and that role determines their experience across the entire application.',
    keyFeatures: [
      {
        label: 'Role list with user counts',
        detail: 'See all active and inactive roles at a glance, including how many users are assigned to each.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        label: 'Create and duplicate roles',
        detail: 'Create a new role from scratch or duplicate an existing one to use as a starting point.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        label: 'Edit role permissions',
        detail: 'Open any role to configure its permissions across projects, reports, and settings.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        label: 'Manage role users',
        detail: 'Assign or remove users from a role directly from the role detail page.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        label: 'Activate and deactivate roles',
        detail: 'Deactivate a role to prevent it from being assigned to new users without deleting it.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Role summary cards / table',
        description: 'Lists each role with its name, type (Platform or Custom), status (Active/Inactive), user count, and action buttons.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        name: 'Search bar',
        description: 'Filters the role list by name as you type.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        name: 'Stats row',
        description: 'Shows total roles, active roles, and total users assigned across all roles.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        name: 'Create Role button',
        description: 'Opens the role creation form to define a new custom role from scratch.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        name: 'Edit (pencil) icon',
        description: 'Opens the role detail page where you can edit the role name, description, and all permission settings.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        name: 'Duplicate (copy) icon',
        description: 'Creates a copy of the role with all its permissions, which you can then rename and adjust.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
      {
        name: 'Delete (trash) icon',
        description: 'Deletes a custom role. Platform roles cannot be deleted.',
        requiredPermission: 'settings.rolesAndPermissions',
      },
    ],
    useCases: [
      'An administrator creates a new "Finance Reviewer" role with read-only access to financial reports but no project editing rights.',
      'A team lead duplicates the Project Manager role to create a "Senior PM" role with additional settings access.',
      'An administrator deactivates a role that is no longer used to keep the list tidy without losing its configuration.',
      'A new user joins and needs to be assigned the correct role before they log in for the first time.',
    ],
  },

  userGuide: {
    gettingStarted:
      'Review the existing platform roles before creating new ones. Platform roles (Administrator, Executive, Project Manager, Project Team) cover most standard use cases — create custom roles only when you need a unique combination of permissions.',
    steps: [
      {
        title: 'How do I create a new role?',
        requiredPermission: 'settings.rolesAndPermissions',
        items: [
          { text: 'Click Create Role in the top-right area of the page.' },
          {
            text: 'Enter a role name and description.',
            note: 'Role names must be unique. Descriptions are visible to administrators only.',
          },
          { text: 'Configure the permissions across each tab: Project Visibility, Project Actions, Project Tabs, Reports & Analytics, and Settings.' },
          { text: 'Click Save to create the role.' },
          {
            text: 'The new role appears in the list and can be assigned to users immediately.',
            tip: 'If the new role is similar to an existing one, use Duplicate instead of creating from scratch.',
          },
        ],
      },
      {
        title: 'How do I duplicate a role?',
        requiredPermission: 'settings.rolesAndPermissions',
        items: [
          { text: 'Find the role you want to copy in the list.' },
          { text: 'Click the copy icon next to the role name.' },
          {
            text: 'A new role is created with the name "Copy of [original name]" and all the same permissions.',
            tip: 'Rename the duplicate immediately to avoid confusion.',
          },
          { text: 'Click the edit icon on the duplicated role to rename it and adjust any permissions.' },
        ],
      },
      {
        title: 'How do I edit a role\'s permissions?',
        requiredPermission: 'settings.rolesAndPermissions',
        items: [
          { text: 'Click the edit (pencil) icon next to the role you want to change.' },
          { text: 'Navigate through the permission tabs: Project Visibility, Project Actions, Project Tabs, Reports & Analytics, and Settings.' },
          { text: 'Make the required changes.' },
          {
            text: 'Click Save Role.',
            note: 'Changing a role\'s permissions affects all users currently assigned to that role immediately.',
          },
        ],
      },
      {
        title: 'How do I delete a role?',
        requiredPermission: 'settings.rolesAndPermissions',
        items: [
          {
            text: 'Click the delete (trash) icon next to a custom role.',
            note: 'Platform roles (Administrator, Executive, Project Manager, Project Team) cannot be deleted.',
          },
          {
            text: 'Confirm the deletion in the dialog that appears.',
            note: 'Roles that have users assigned to them cannot be deleted. Reassign all users to another role first.',
          },
        ],
      },
    ],
    proTips: [
      'Platform roles are maintained by the system and cannot be deleted or renamed. They cover the four standard permission profiles used by most companies.',
      'When you change a role\'s permissions, all users with that role are affected immediately — no logout or page refresh required on their end.',
      'Use descriptive role names that reflect what the person does, not their seniority level, so new administrators can understand the purpose at a glance.',
      'Deactivate a role rather than deleting it if you might need it again in the future — all its configuration is preserved.',
    ],
  },

  faq: [
    {
      question: 'Can I delete a platform role like Administrator or Project Manager?',
      answer: 'No. Platform roles are protected and cannot be deleted or renamed. You can create custom roles based on them by using the Duplicate function.',
      requiredPermission: 'settings.rolesAndPermissions',
    },
    {
      question: 'What happens to users when I change a role\'s permissions?',
      answer:
        'The change takes effect immediately for all users assigned to that role. They will see the updated permissions on their next page load — no re-login required.',
      requiredPermission: 'settings.rolesAndPermissions',
    },
    {
      question: 'Can I assign a user to more than one role?',
      answer:
        'No. Each user is assigned to exactly one role. If a user needs a unique combination of permissions, create a custom role for them.',
      requiredPermission: 'settings.rolesAndPermissions',
    },
    {
      question: 'What does Project Visibility "Managed & Allocated" mean?',
      answer:
        'It restricts the user to only seeing projects where they are listed as a key person (e.g. Project Manager) or are allocated as a resource. They cannot see all company projects.',
    },
    {
      question: 'How do I control which project tabs a role can access?',
      answer:
        'Edit the role and go to the Project Tabs permission section. For each tab you can set access to Hidden, View, or Edit.',
      requiredPermission: 'settings.rolesAndPermissions',
    },
    {
      question: 'Can I see which users are assigned to a role?',
      answer:
        'Yes. Open the role by clicking the edit icon. The Users section on the role detail page lists all users currently assigned to it.',
      requiredPermission: 'settings.rolesAndPermissions',
    },
  ],

  relatedPages: [
    { label: 'General Settings', path: '/settings/general' },
    { label: 'Project Settings', path: '/settings/project' },
    { label: 'Settings Hub', path: '/settings' },
  ],
};
